import { firebase } from 'common/src/config/configureFirebase';
import { ref, set, get } from 'firebase/database';

// Load config for API endpoints
const config = require('../config.json');
const API_BASE_URL = config.api_endpoints.ecommerce_base_url + '/v1/points';

class LoyaltyAPI {
  static async getAuthHeaders() {
    try {
      const { auth } = firebase;

      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const idToken = await auth.currentUser.getIdToken(true);

      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw new Error('Authentication failed');
    }
  }

  static async makeAuthenticatedRequest(url, options = {}) {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  static async getLoyaltyPointsConfig() {
    try {
      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/settings`);
    } catch (error) {
      console.error('Error fetching loyalty points config:', error);
      throw error;
    }
  }

  static async updateLoyaltyPointsConfig(configData) {
    try {
      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        body: JSON.stringify(configData)
      });
    } catch (error) {
      console.error('Error updating loyalty points config:', error);
      throw error;
    }
  }

  static async getUserLoyaltyPoints(filters = {}) {
    try {
      const params = new URLSearchParams();

      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all' && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/users?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching user loyalty points:', error);
      throw error;
    }
  }

  static async addManualPoints(userId, points, reason = '') {
    try {
      const result = await this.makeAuthenticatedRequest(`${API_BASE_URL}/manual`, {
        method: 'POST',
        body: JSON.stringify({
          userId,
          points,
          reason
        })
      });

      // ✅ FIX: Trigger Firebase update to notify mobile app via real-time listener
      try {
        const { database } = firebase;
        
        // Get user email for realtime_data path (same as customer screen)
        const usersRef = ref(database, `users/${userId}`);
        const userSnapshot = await get(usersRef);
        const userData = userSnapshot.val();
        
        if (userData && userData.email) {
          // Convert email to Firebase-safe key (same format as mobile app)
          const emailKey = userData.email.replace(/\./g, "_").replace("@", "_");
          
          // Push to realtime_data with forceRefresh flag (INSTANT UPDATE!)
          const realtimeRef = ref(database, `realtime_data/${emailKey}`);
          await set(realtimeRef, {
            totalPoints: result.totalPoints || (result.user?.totalPoints) || 0,
            lastActivity: Date.now(),
            source: 'manual_adjustment',
            reason: reason || 'Admin adjustment',
            updatedAt: Date.now(),
            refresh: {
              forceRefresh: true, // ← CRITICAL: Triggers instant update!
              lastRefresh: Date.now(),
            },
          });

          console.log('✅ Firebase real-time trigger sent to realtime_data/' + emailKey);
        } else {
          console.warn('⚠️ User email not found, cannot send realtime trigger');
        }
        
        // Also update userPoints path as backup (for driver with old implementation)
        const userPointsRef = ref(database, `userPoints/${userId}`);
        await set(userPointsRef, {
          totalPoints: result.totalPoints || (result.user?.totalPoints) || 0,
          lastActivity: Date.now(),
          source: 'manual_adjustment',
          reason: reason || 'Admin adjustment',
          updatedAt: Date.now(),
        });

      } catch (firebaseError) {
        console.warn('⚠️ Failed to trigger Firebase update (points still saved in API):', firebaseError);
        // Don't throw - points are already saved in main API
      }

      return result;
    } catch (error) {
      console.error('Error adding manual points:', error);
      throw error;
    }
  }

  static async subtractManualPoints(userId, points, reason = '') {
    try {
      const result = await this.makeAuthenticatedRequest(`${API_BASE_URL}/manual`, {
        method: 'POST',
        body: JSON.stringify({
          userId,
          points: -points, // Make it negative for subtraction
          reason
        })
      });

      // ✅ FIX: Trigger Firebase update to notify mobile app via real-time listener
      try {
        const { database } = firebase;
        
        // Get user email for realtime_data path (same as customer screen)
        const usersRef = ref(database, `users/${userId}`);
        const userSnapshot = await get(usersRef);
        const userData = userSnapshot.val();
        
        if (userData && userData.email) {
          // Convert email to Firebase-safe key (same format as mobile app)
          const emailKey = userData.email.replace(/\./g, "_").replace("@", "_");
          
          // Push to realtime_data with forceRefresh flag (INSTANT UPDATE!)
          const realtimeRef = ref(database, `realtime_data/${emailKey}`);
          await set(realtimeRef, {
            totalPoints: result.totalPoints || (result.user?.totalPoints) || 0,
            lastActivity: Date.now(),
            source: 'manual_adjustment',
            reason: reason || 'Admin deduction',
            updatedAt: Date.now(),
            refresh: {
              forceRefresh: true, // ← CRITICAL: Triggers instant update!
              lastRefresh: Date.now(),
            },
          });

          console.log('✅ Firebase real-time trigger sent to realtime_data/' + emailKey);
        } else {
          console.warn('⚠️ User email not found, cannot send realtime trigger');
        }
        
        // Also update userPoints path as backup
        const userPointsRef = ref(database, `userPoints/${userId}`);
        await set(userPointsRef, {
          totalPoints: result.totalPoints || (result.user?.totalPoints) || 0,
          lastActivity: Date.now(),
          source: 'manual_adjustment',
          reason: reason || 'Admin deduction',
          updatedAt: Date.now(),
        });

      } catch (firebaseError) {
        console.warn('⚠️ Failed to trigger Firebase update (points still saved in API):', firebaseError);
        // Don't throw - points are already saved in main API
      }

      return result;
    } catch (error) {
      console.error('Error subtracting manual points:', error);
      throw error;
    }
  }

  static async getLoyaltyStats() {
    try {
      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/stats`);
    } catch (error) {
      console.error('Error fetching loyalty stats:', error);
      throw error;
    }
  }

  static async getPointsHistory(filters = {}) {
    try {
      const params = new URLSearchParams();

      // Add filters to query params
      if (filters.type && filters.type !== 'All') {
        params.append('type', filters.type.toLowerCase());
      }
      if (filters.keyword) {
        params.append('keyword', filters.keyword);
      }
      if (filters.userId) {
        params.append('user_id', filters.userId);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }

      const finalUrl = `${API_BASE_URL}/history?${params.toString()}`;
      return await this.makeAuthenticatedRequest(finalUrl);
    } catch (error) {
      console.error('Error fetching points history:', error);
      throw error;
    }
  }

  static async getPointsHistoryPagination(filters = {}) {
    try {
      const params = new URLSearchParams();

      // Add filters to query params
      if (filters.type && filters.type !== 'All') {
        params.append('type', filters.type.toLowerCase());
      }
      if (filters.keyword) {
        params.append('keyword', filters.keyword);
      }
      if (filters.userId) {
        params.append('user_id', filters.userId);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }
      if (filters.grouped !== undefined) {
        params.append('grouped', filters.grouped);
      }

      const finalUrl = `${API_BASE_URL}/history/pagination?${params.toString()}`;
      return await this.makeAuthenticatedRequest(finalUrl);
    } catch (error) {
      console.error('Error fetching points history pagination:', error);
      throw error;
    }
  }

  static async createPoints(userId, points) {
    try {
      return await this.makeAuthenticatedRequest(`${API_BASE_URL}`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          points: points
        })
      });
    } catch (error) {
      console.error('Error creating points:', error);
      throw error;
    }
  }

  static async getUsers(data = {}) {
    try {
      const params = new URLSearchParams();
      if (data.search) {
        params.append('search', data.search);
      }
      if (data.page) {
        params.append('page', data.page);
      }
      if (data.per_page) {
        params.append('per_page', data.per_page);
      }

      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/users?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  static async getGroupedPoints(filters = {}) {
    try {
      const params = new URLSearchParams();

      // Add grouped parameter
      params.append('grouped', 'true');

      // Add filters to query params
      if (filters.type && filters.type !== 'All') {
        params.append('type', filters.type.toLowerCase());
      }
      if (filters.keyword) {
        params.append('keyword', filters.keyword);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }

      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/history?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching grouped points:', error);
      throw error;
    }
  }

  static async getUserHistory(userId, options = {}) {
    try {
      const params = new URLSearchParams();
      params.append('user_id', userId);

      if (options.page) {
        params.append('page', options.page);
      }
      if (options.limit) {
        params.append('limit', options.limit);
      }

      return await this.makeAuthenticatedRequest(`${API_BASE_URL}/history?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching user history:', error);
      throw error;
    }
  }
}

export default LoyaltyAPI; 